import { useApolloClient } from '@apollo/client';
import {
  Accordion,
  AccordionItem,
  createUpdateGQLFragmentGenerator,
  Details,
  Filters,
  type FilterValues,
  ToggleButtonField,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { Field, useFormikContext } from 'formik';
import gql from 'graphql-tag';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../../../../config';
import {
  ActivePermissionsDocument,
  type ActivePermissionsQuery,
} from '../../../../generated/id-service-access-management';
import {
  type Mutation,
  type MutationCreateServiceAccountPermissionAssignmentArgs,
  type MutationDeleteServiceAccountPermissionAssignmentArgs,
  useServiceAccountPermissionsQuery,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { environmentApolloClient } from '../../../environmentApolloClient';
import {
  isManagedFilter,
  permissionNameFilter,
  statusFilter,
} from './PermissionStations/filters';
import {
  type PermissionList,
  useFilters,
  usePermissionList,
} from './PermissionStations/hooks';
import type {
  PermissionFilters,
  PermissionFormData,
  PermissionMap,
} from './PermissionStations/models';
import classes from './ServiceAccountPermissions.module.scss';

const hiddenPermissionServiceIds = ['ax-key-service', 'ax-entitlement-service'];

const Form: React.FC<{
  permissionMap: PermissionMap[];
  filters: FilterValues<PermissionFilters>;
}> = ({ permissionMap, filters }) => {
  const { values } = useFormikContext<PermissionFormData>();
  const { filteredPermissions } = useFilters(permissionMap, values, filters);

  if (Object.keys(values).length > 0) {
    return (
      // Add small padding on accordion
      <Accordion
        expandedByDefault={true}
        alignAccordionItem={false}
        header={
          <div className={classes.accordionHeaderColumns}>
            <p>Permission Name</p>
            <p>Assigned</p>
          </div>
        }
      >
        {filteredPermissions.map((service: PermissionMap) => {
          return (
            <AccordionItem
              key={service.service.id}
              header={<b>{service.service.label}</b>}
            >
              <div className={classes.rowContainer}>
                {service.service.children.map((child) => {
                  const { id, label, name } = child;
                  const { isAssigned } = values[id];
                  return (
                    <div
                      key={id}
                      className={clsx(classes.accordionColumns, {
                        [classes.assigned]: isAssigned,
                      })}
                    >
                      <p title={name}>{label}</p>
                      <Field
                        name={`${id}.isAssigned`}
                        as={ToggleButtonField}
                        inlineMode={true}
                        height={46}
                        width={92}
                      />
                    </div>
                  );
                })}
              </div>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  } else {
    return <></>;
  }
};

export const ServiceAccountPermissions: React.FC = () => {
  const { envId, id } = useParams<{
    envId: string;
    id: string;
  }>();

  const apolloClient = useApolloClient();

  // Query service account permissions
  const { loading, data, error } = useServiceAccountPermissionsQuery({
    client: apolloClient,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  // Query active permissions
  const [activePermissions, setActivePermissions] = useState<PermissionList>(
    [],
  );

  const [filters, onFiltersChangeHandler] = useState<
    FilterValues<PermissionFilters>
  >({});

  useEffect(() => {
    (async () => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.accessManagementBaseUrl).toString(),
      );

      const result = await envApolloClient.query<ActivePermissionsQuery>({
        query: ActivePermissionsDocument,
        variables: {
          filter: {
            serviceId: {
              notIn: hiddenPermissionServiceIds,
            },
          },
        },
        fetchPolicy: 'no-cache',
      });

      setActivePermissions(result.data.activePermissions?.nodes ?? []);
    })();
  }, [apolloClient, envId]);

  const { permissionMap, formData } = usePermissionList(
    activePermissions,
    data?.serviceAccount?.serviceAccountPermissionAssignments.nodes ?? [],
  );

  const onSubmit = useCallback(
    async (formData: PermissionFormData): Promise<void> => {
      const mutations: string[] = [];

      const generateUpdateGQLFragment =
        createUpdateGQLFragmentGenerator<Mutation>();

      const generateCreateMutation = (permissionId: string): string =>
        generateUpdateGQLFragment<MutationCreateServiceAccountPermissionAssignmentArgs>(
          'createServiceAccountPermissionAssignment',
          {
            input: {
              serviceAccountPermissionAssignment: {
                serviceAccountId: id,
                permissionId,
                environmentId: envId,
              },
            },
          },
        );

      const generateDeleteMutation = (assignmentId: string): string =>
        generateUpdateGQLFragment<MutationDeleteServiceAccountPermissionAssignmentArgs>(
          'deleteServiceAccountPermissionAssignment',
          {
            input: { id: assignmentId },
          },
        );

      Object.entries(formData ?? {}).forEach(([permissionId, props], idx) => {
        const { assignmentId, isAssigned } = props;

        // assigned on server
        if (assignmentId !== undefined) {
          if (!isAssigned) {
            mutations.push(
              `assign${idx}:${generateDeleteMutation(assignmentId)}`,
            );
          }

          // not assigned on server
        } else if (assignmentId === undefined) {
          if (isAssigned) {
            mutations.push(
              `assign${idx}:${generateCreateMutation(permissionId)}`,
            );
          }
        }
      });

      const GqlDoc = gql`mutation PermissionAssignments {
        ${mutations}
      }`;

      // submit mutations to the back end
      await apolloClient.mutate({ mutation: GqlDoc });
    },
    [id, envId, apolloClient],
  );

  return (
    <Details<PermissionFormData>
      defaultTitle={`${data?.serviceAccount?.name ?? ''} Permissions`}
      initialData={{
        data: formData,
        loading: loading,
        entityNotFound: data?.serviceAccount === null,
        error: error?.message,
      }}
      saveData={onSubmit}
      edgeToEdgeContent={true}
    >
      <div className={classes.wrapper}>
        <Filters<PermissionFilters>
          options={[permissionNameFilter, isManagedFilter, statusFilter]}
          onFiltersChange={onFiltersChangeHandler}
        />
        {!loading && data?.serviceAccount !== null && (
          <Form permissionMap={permissionMap} filters={filters} />
        )}
      </div>
    </Details>
  );
};

export const ServiceAccountPermissionsCrumb: BreadcrumbResolver = () =>
  'Permissions';
